import React, { Component } from 'react';

import { Card, Callout, Flex, Grid, Icon, Text } from 'core/components';
import { formatBytesGreek } from 'core/util';
import { inject, observer } from 'mobx-react';
import { ReactComponent as MemoryIcon } from 'app/assets/icons/memory_icon.svg';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { getHealthStateFromValue } from 'app/views/hybrid/utils/health';
import DeviceMetricsDetailCharts from 'app/views/hybrid/maps/components/deviceMetrics/DeviceMetricsDetailCharts';

// Legacy flow-based query for memory metrics
const createFlowQuery = (deviceName) => ({
  all_devices: false,
  device_name: [deviceName],
  fastData: 'Full',
  metric: ['ktappprotocol__snmp_device_metrics__STR01'], // component
  aggregateTypes: [
    'avg_ktappprotocol__snmp_device_metrics__INT64_04', // utilization
    'avg_ktappprotocol__snmp_device_metrics__INT64_02', // used
    'avg_ktappprotocol__snmp_device_metrics__INT64_01' // total
  ],
  lookback_seconds: 86400,
  show_overlay: false,
  show_total_overlay: false
});

// Modern kmetrics query for memory (used by ranger)
const createKmetricsQuery = (deviceId) => {
  // Early return if deviceId is not available - invalid query
  if (!deviceId) {
    return null;
  }

  const filterValue = String(deviceId);

  const query = {
    use_kmetrics: true,
    show_overlay: false,
    show_total_overlay: false,
    kmetrics: {
      measurement: '/components/memory', // Component breakdown
      dimensions: ['device_id', 'index', 'name'], // Component dimensions
      metrics: [
        { name: 'utilization', type: 'gauge' } // Correct metric
      ],
      range: {
        lookback: 'PT3600S' // 1 hour
      },
      rollups: {
        avg_utilization: {
          metric: 'utilization',
          aggregate: 'avg'
        }
      },
      sort: [{ name: 'device_id', direction: 'asc' }],
      limit: 100,
      includeTimeseries: 20,
      filters: {
        connector: 'All',
        filterGroups: [
          {
            connector: 'All',
            filters: [
              {
                filterField: 'device_id',
                operator: '=',
                filterValue
              }
            ]
          }
        ]
      },
      viz: {
        metric: 'utilization'
      }
    }
  };
  return query;
};

@inject('$devices')
@observer
export default class MemoryChart extends Component {
  static defaultProps = {
    title: 'Average Memory Utilization'
  };

  state = (() => {
    const { $devices, deviceName } = this.props;
    return {
      device: $devices.byName(deviceName),
      kmetricsFailed: false
    };
  })();

  componentDidUpdate(prevProps) {
    const { $devices, deviceName } = this.props;
    const prevDevice = prevProps.$devices?.byName(prevProps.deviceName);
    const currentDevice = $devices.byName(deviceName);

    const prevDeviceId = prevDevice?.get('id');
    const currentDeviceId = currentDevice?.get('id');

    // Only update if device IDs are actually different
    if (currentDeviceId !== prevDeviceId && (currentDeviceId || prevDeviceId)) {
      this.setState({ device: currentDevice, kmetricsFailed: false });
    }
  }

  get flowQuery() {
    const { deviceName } = this.props;
    return createFlowQuery(deviceName);
  }

  get kmetricsQuery() {
    const { device } = this.state;
    if (!device) {
      return null;
    }
    const deviceId = device.get('id');
    // createKmetricsQuery will return null if deviceId is invalid
    return createKmetricsQuery(deviceId);
  }

  get query() {
    const { kmetricsFailed } = this.state;
    // If kmetrics has failed or is unavailable, use flow query instead
    const { kmetricsQuery } = this;
    return kmetricsFailed || !kmetricsQuery ? this.flowQuery : kmetricsQuery;
  }

  get loadingState() {
    return <Card height={100} mb={1} showSkeleton />;
  }

  get emptyState() {
    return (
      <Callout>
        <Icon icon={MemoryIcon} />
        <Text ml={1}>Memory Data Unavailable</Text>
      </Callout>
    );
  }

  getFlowResults = (results) => {
    const rawRows = results.getRawDataRows();

    return rawRows.map((model) => {
      const value = model.get('avg_ktappprotocol__snmp_device_metrics__INT64_04');
      const healthState = getHealthStateFromValue({ type: 'memory', value });
      let color = 'indigo3';
      let headingProps = {};

      if (healthState.state !== 'GOOD') {
        color = healthState.color;
        headingProps = { color: healthState.color, fontWeight: 'bold' };
      }

      return {
        key: model.get('key'),
        usedValue: model.get('avg_ktappprotocol__snmp_device_metrics__INT64_02'),
        totalValue: model.get('avg_ktappprotocol__snmp_device_metrics__INT64_01'),
        value,
        usedLastDatapoint: model.get('f_avg_int64_02__k_last'),
        totalLastDatapoint: model.get('f_avg_int64_01__k_last'),
        valueLastDatapoint: model.get('f_avg_int64_04__k_last'),
        rawData: model.get('rawData').f_avg_int64_04.flow,
        color,
        headingProps
      };
    });
  };

  getKmetricsResults = (results) => {
    // For kmetrics, use results.models directly instead of getRawDataRows()
    const models = results.models || [];

    return models.map((model) => {
      const value = model.get('avg_utilization'); // Average memory utilization
      // For kmetrics, timeseries data is in the 'timeseries' field
      // Format: [[timestamp_ms, utilization_value], ...]
      let rawData = model.get('timeseries') || [];

      // Transform rawData to ensure it's a simple array of [x, y] pairs
      // This prevents Highcharts from treating each point as an object with marker properties
      rawData = rawData.map((point) => {
        if (Array.isArray(point)) {
          // Create a new object to avoid any attached properties
          return {
            x: Number(point[0]),
            y: Number(point[1]),
            marker: { enabled: false }
          };
        }
        // If it's an object, extract x and y
        return {
          x: Number(point.x),
          y: Number(point.y),
          marker: { enabled: false }
        };
      });

      // Use the 'name' dimension as the component name
      const componentName = model.get('name');

      const healthState = getHealthStateFromValue({ type: 'memory', value });
      let color = 'indigo3';
      let headingProps = {};

      if (healthState.state !== 'GOOD') {
        color = healthState.color;
        headingProps = { color: healthState.color, fontWeight: 'bold' };
      }

      return {
        key: componentName || model.get('key'),
        value,
        lastDatapoint: value,
        rawData,
        color,
        headingProps
      };
    });
  };

  handleQueryComplete = ({ results, fullyLoaded }) => {
    const { kmetricsFailed } = this.state;
    const modelCount = results?.models?.length || 0;

    // If kmetrics returned no data and is fully loaded, mark it as failed
    // This will cause the query getter to switch to flow query on next render
    if (modelCount === 0 && fullyLoaded && !kmetricsFailed) {
      this.setState({ kmetricsFailed: true });
    }
  };

  getResults = (results) => {
    const { kmetricsFailed } = this.state;
    // Use results processor based on which query mode is active
    return kmetricsFailed ? this.getFlowResults(results) : this.getKmetricsResults(results);
  };

  render() {
    const { title } = this.props;

    return (
      <LightweightDataViewWrapper query={this.query} onQueryComplete={this.handleQueryComplete}>
        {({ loading, results }) => {
          if (loading) {
            return this.loadingState;
          }

          const data = this.getResults(results);

          if (data.length > 0) {
            return (
              <Grid gridTemplateColumns="repeat(auto-fit, minmax(350px, 1fr))" gridGap={4} gridRowGap={2}>
                <DeviceMetricsDetailCharts
                  data={data}
                  title={title}
                  icon={MemoryIcon}
                  renderFooter={({ usedValue, totalValue }) => {
                    // Only show footer if flow data has usedValue/totalValue
                    if (!usedValue && !totalValue) {
                      return null;
                    }
                    return (
                      <Flex justifyContent="flex-end" mt={1}>
                        <Text fontWeight="bold" mr={2}>
                          Used:{' '}
                          <Text fontWeight="normal" muted>
                            {formatBytesGreek(usedValue)}
                          </Text>
                        </Text>
                        <Text fontWeight="bold">
                          Total:{' '}
                          <Text fontWeight="normal" muted>
                            {formatBytesGreek(totalValue)}
                          </Text>
                        </Text>
                      </Flex>
                    );
                  }}
                />
              </Grid>
            );
          }

          return this.emptyState;
        }}
      </LightweightDataViewWrapper>
    );
  }
}
